// src/components/HelpToggle.jsx
// 社員側の全機能画面に共通で表示する「説明使用／説明不要」トグル（右上・固定）。
// EmployeeRoute に1箇所だけ組み込めば、全画面＋今後追加される画面にも自動で効く。
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../services/api";
import HelpPdfModal from "./HelpPdfModal";
import "./HelpToggle.css";

/* ========== 説明データのキャッシュ（画面遷移のたびに再取得しない） ========== */
let _cache = null;      // { helps: {key: {title, pdf_url}}, settings: {key: boolean} }
let _inflight = null;

export function resetHelpCache() {
  _cache = null;
  _inflight = null;
}

async function loadHelpData() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    const [helpRes, setRes] = await Promise.all([
      api.get("/api/help/"),
      api.get("/api/employee/me/help-settings/").catch(() => ({ data: {} })),
    ]);
    const helps = {};
    const list = Array.isArray(helpRes.data) ? helpRes.data : [];
    list.forEach((h) => { if (h?.feature_key) helps[h.feature_key] = h; });
    _cache = { helps, settings: (setRes && setRes.data) || {} };
    _inflight = null;
    return _cache;
  })();
  return _inflight;
}

/** URL から機能キーを導出（動的パラメータを除去）。例: /event/123 → event, /worktime/record → worktime/record */
export function featureKeyFromPath(pathname) {
  const parts = String(pathname || "").split("/").filter(Boolean);
  const cleaned = parts.filter(
    (p) => !/^\d+$/.test(p) && !/^[0-9a-fA-F-]{16,}$/.test(p)
  );
  return cleaned.join("/");
}

/* ========== 本体 ========== */
export default function HelpToggle() {
  const { pathname } = useLocation();
  const featureKey = useMemo(() => featureKeyFromPath(pathname), [pathname]);

  const [help, setHelp] = useState(null);   // この画面の説明PDF（未登録なら null）
  const [enabled, setEnabled] = useState(true);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    setHelp(null);
    setOpen(false);
    loadHelpData()
      .then((d) => {
        if (!alive) return;
        const h = d.helps[featureKey] || null;
        setHelp(h);
        if (!h) return;
        const on = d.settings[featureKey] !== false; // キーが無ければ既定ON
        setEnabled(on);
        setOpen(on);                                  // ONなら自動表示
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [featureKey]);

  // 説明PDFが登録されていない画面ではトグルごと非表示
  if (!help) return null;

  const setPref = async (on) => {
    setEnabled(on);
    setOpen(on);
    if (_cache) _cache.settings[featureKey] = on;
    try {
      await api.patch("/api/employee/me/help-settings/", {
        feature_key: featureKey,
        enabled: on,
      });
    } catch {
      /* 保存に失敗しても画面表示は維持（次回リロードで元に戻る） */
    }
  };

  return (
    <>
      <div className="help-toggle" role="group" aria-label="説明の表示切替">
        <button
          type="button"
          className={`help-toggle-btn${enabled ? " is-active" : ""}`}
          onClick={() => setPref(true)}
        >
          説明使用
        </button>
        <button
          type="button"
          className={`help-toggle-btn${!enabled ? " is-active" : ""}`}
          onClick={() => setPref(false)}
        >
          説明不要
        </button>
      </div>

      {open && (
        <HelpPdfModal
          title={help.title}
          url={help.pdf_url}
          onClose={() => setOpen(false)}
          onDisable={() => setPref(false)}
        />
      )}
    </>
  );
}
